import {
  User,
  UserRoles,
  UserStatus,
  UserOrganizationRoleStatus,
  createOrUpdateUserRequest,
  disableOrEnableUserRequest,
  getPaginatedUsersRequest,
  updateUserOrganizationStatusRequest,
  getSelfWithOrganizationRequest,
} from '@models/user.model';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { normalizeEmail } from '@utils/normalizeEmail';

export interface GetPaginatedUsersParams {
  page: number;
  limit: number;
  search?: string;
  filter?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  role?: string | null;
  filterOrganizationId?: string | null;
  createdAtFrom?: string | null;
}

export interface CreateUserPayload {
  organizationId: string;
  status: UserStatus;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRoles;
}

export interface UpdateUserPayload {
  userId: string;
  // No email: it is not updatable through this mutation. See updateUser.
  firstName: string;
  lastName: string;
  // Optional: omitting it updates the profile and leaves every membership as it
  // is. The Admin Users modal manages roles per organization on its own, so it
  // submits without a role.
  role?: UserRoles;
  // Organization in which the role applies. Without it the backend falls back to
  // the caller's own X-Organization-ID header, which for a SUPER_ADMIN editing
  // another org's user is the wrong organization (or none at all).
  organizationId?: string;
}

export const userService = {
  async getPaginatedUsers(params: GetPaginatedUsersParams): Promise<{
    users: User[];
    count: number;
  }> {
    const variables = {
      users: {
        page: params.page,
        limit: params.limit,
        search: params.search || undefined,
        filter: params.filter || 'Active',
        orderBy: params.orderBy || 'name',
        orderDirection: params.orderDirection || 'asc',
        role: params.role || undefined,
        filterOrganizationId: params.filterOrganizationId || undefined,
        createdAtFrom: params.createdAtFrom || undefined,
      },
    };

    const data: getPaginatedUsersRequest = await graphQlClient.request(
      UserGraphQL.queries.getPaginatedUsers,
      variables,
    );

    if (!data?.getPaginatedUsers) {
      throw new Error('Failed to fetch paginated users');
    }

    return {
      users: data.getPaginatedUsers.users || [],
      count: data.getPaginatedUsers.count || 0,
    };
  },

  async createUser(payload: CreateUserPayload): Promise<User> {
    if (!payload.email) throw new Error('Email is required');
    if (!payload.organizationId) throw new Error('Organization ID is required');

    const variables = {
      user: {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: normalizeEmail(payload.email),
        role: payload.role,
        organizationId: payload.organizationId,
        status: payload.status,
      },
    };

    const data: createOrUpdateUserRequest = await graphQlClient.request(
      UserGraphQL.mutations.createUser,
      variables,
    );

    if (!data?.createUser) throw new Error('Failed to create user');
    return data.createUser;
  },

  async updateUser(payload: UpdateUserPayload): Promise<User> {
    if (!payload.userId) throw new Error('User ID is required');

    // Email is deliberately NOT sent: UserUpdateInput does not declare the field,
    // so including it made the server reject the whole mutation. It could not
    // have worked anyway — UserService.update whitelists the columns it writes
    // and email is not among them, because changing it here would leave the
    // Cognito identity pointing at the old address. The modal disables the email
    // field while editing for the same reason.
    const userVariables = {
      user: {
        userId: payload.userId,
        firstName: payload.firstName,
        lastName: payload.lastName,
      },
    };

    const userData: createOrUpdateUserRequest = await graphQlClient.request(
      UserGraphQL.mutations.updateOtherUser,
      userVariables,
    );

    if (!userData?.updateOtherUser) throw new Error('Failed to update user');

    // Then, update the user's role if provided
    if (payload.role) {
      const roleVariables = {
        input: {
          userId: payload.userId,
          role: payload.role,
          ...(payload.organizationId && { organizationId: payload.organizationId }),
        },
      };

      const roleData: createOrUpdateUserRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateUserOrganizationRole,
        roleVariables,
      );

      if (!roleData?.updateUserOrganizationRole) throw new Error('Failed to update user role');
      return roleData.updateUserOrganizationRole;
    }

    return userData.updateOtherUser;
  },

  async disableUser(userId: string): Promise<User> {
    if (!userId) throw new Error('User ID is required');

    const data: disableOrEnableUserRequest = await graphQlClient.request(
      UserGraphQL.mutations.disableUser,
      { id: userId },
    );

    if (!data?.disableUser) throw new Error('Failed to disable user');
    return data.disableUser;
  },

  async enableUser(userId: string): Promise<User> {
    if (!userId) throw new Error('User ID is required');

    const data: disableOrEnableUserRequest = await graphQlClient.request(
      UserGraphQL.mutations.enableUser,
      { id: userId },
    );

    if (!data?.enableUser) throw new Error('Failed to enable user');
    return data.enableUser;
  },

  /**
   * Change a user's role in ONE organization, which may be a different tenant
   * from the caller's active one (SUPER_ADMIN only — the server rejects everyone
   * else when organizationId doesn't match the X-Organization-ID header).
   *
   * Distinct from updateUser: that one also writes profile fields through
   * updateEmployee, which refuses targets outside the header organization.
   */
  async updateUserOrganizationRole(userId: string, role: string, organizationId?: string): Promise<User> {
    if (!userId) throw new Error('User ID is required');
    if (!role) throw new Error('Role is required');

    const variables = {
      input: {
        userId,
        role,
        ...(organizationId && { organizationId }),
      },
    };

    const data: createOrUpdateUserRequest = await graphQlClient.request(
      UserGraphQL.mutations.updateUserOrganizationRole,
      variables,
    );

    if (!data?.updateUserOrganizationRole) throw new Error('Failed to update user role');
    return data.updateUserOrganizationRole;
  },

  async updateUserOrganizationStatus(
    userId: string,
    status: UserOrganizationRoleStatus,
    organizationId?: string,
  ): Promise<User> {
    if (!userId) throw new Error('User ID is required');
    if (!status) throw new Error('Status is required');

    const variables = {
      input: {
        userId,
        status,
        ...(organizationId && { organizationId }),
      },
    };

    const data: updateUserOrganizationStatusRequest = await graphQlClient.request(
      UserGraphQL.mutations.updateUserOrganizationStatus,
      variables,
    );

    if (!data?.updateUserOrganizationStatus) throw new Error('Failed to update user status');
    return data.updateUserOrganizationStatus;
  },

  /**
   * Get current user with their organization data
   * Used to check organization status and refresh user state
   */
  async getSelfWithOrganization(): Promise<{
    user: User;
    organization: {
      name: string;
      organizationId: string;
      status?: string;
    } | null;
  }> {
    const data: getSelfWithOrganizationRequest = await graphQlClient.request(
      UserGraphQL.queries.getSelfWithOrganization,
    );

    if (!data?.getSelfWithOrganization) {
      throw new Error('Failed to fetch user data');
    }

    return {
      user: data.getSelfWithOrganization.user,
      organization: data.getSelfWithOrganization.organization || null,
    };
  },
};

export default userService;